import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, IconButton, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProgram, removeProgramPartnerCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ProgramPartnerCategoryListPage() {
  const { businessDomainId, businessUnitId, programId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProgram(programId);
      setRows((d.associatedPartnerCategories || []).map((c) => ({ id: c.partnerCategoryId, ...c })));
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteConfirm = async () => {
    try {
      await removeProgramPartnerCategory(programId, targetRow.partnerCategoryId);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
    setTargetRow(null);
  };

  const filteredRows = searchText
    ? rows.filter((r) => r.partnerCategoryName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "partnerCategoryName", headerName: t("partnerCategory.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: "", width: 60, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error"
          onClick={(e) => { e.stopPropagation(); setTargetRow(p.row); setConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.partnerCategory.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/partner-categories/add`)}>
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid rows={filteredRows} columns={columns} getRowId={(r) => r.id}
        loading={loading} autoHeight pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setTargetRow(null); }} />
    </Box>
  );
}
